import { PDFDocument } from "pdf-lib";
import { showToast } from "../../components/ui/custom-toast";
import { getErrorMessage } from "../../../api/errorMap";
import { useAuthStore, type AuthUser } from "../../../stores/authStore";
import type {
  DocEngineDocumentRecord,
  SupportingEvidence,
  HistoryEvent,
} from "./api/contentRegistryApi";
import {
  DOCUMENT_TYPES,
  LAYER_ROW_STATE,
  CONF_TONES,
  CONFIDENCE_STRONG_THRESHOLD,
  CONFIDENCE_MODERATE_THRESHOLD,
  EMPTY_PLACEHOLDER,
  PCT_GOOD_MIN,
  PCT_OK_MIN,
  STAGE_KEY_ALIASES,
  CATEGORY_ORDER,
  JOB_PHASE,
  type LayerRowState,
  type StrengthLabel,
  type ConsoleTone,
  type Tier,
} from "./constants";
import type {
  IngestStageEvent,
  ValidateFileResult,
  ValidateFileSummary,
  ValidateRuleResult,
} from "./api/contentRegistryApi";
import { PIPELINE_STAGES, HOLD_STAGE_INDEX } from "./processingPipeline";
import type { ConsolePhase } from "./processingPipeline";
import type { GovDocRow, EvidenceRow } from "./types";

export type ConsoleStageState = "active" | "done" | "pending" | "failed";

export interface ConsoleYield {
  label: string;
  value: string;
  hint: string;
  tone: "neutral" | "pass" | "warn" | "fail";
}

/** Coerce an unknown thrown value into a user-facing string. */
export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/* ── Authenticated download helper ── */
export async function triggerDownload(url: string, fileName?: string | null) {
  const token = useAuthStore.getState().getToken();
  try {
    const res = await fetch(url, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      // Errors come back as JSON envelopes; surface the real API message.
      let parsed: unknown = null;
      try {
        parsed = await res.json();
      } catch {
        /* non-JSON error body */
      }
      const apiErr = (
        parsed as { errors?: { title?: string; detail?: string; code?: string }[] }
      )?.errors?.[0];
      // Export permission required; 403 = IDN-0003-E "Permission Denied".
      const isPermissionDenied =
        res.status === 403 ||
        apiErr?.code === "IDN-0003-E" ||
        apiErr?.title === "Permission Denied";
      if (isPermissionDenied) {
        showToast(
          "warning",
          apiErr?.detail ||
          "You don't have permission to download this file. Export permission is required.",
          "Permission Denied",
        );
        return;
      }
      const title = apiErr?.title || "Download failed";
      showToast("error", getErrorMessage(parsed, "Download failed"), title);
      return;
    }
    const disposition = res.headers.get("content-disposition") ?? "";
    const serverName =
      disposition
        .match(/filename[^;=\n]*=["']?([^"';\n]+)["']?/i)?.[1]
        ?.trim() ?? "";
    const name = fileName || serverName || "download";
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  } catch (err) {
    const msg = err instanceof Error ? err.message : "Download failed";
    showToast("error", msg || "Download failed");
  }
}

/** Map backend lifecycle status to StatusBadge variant */
export function mapLifecycleToVariant(
  s: string,
):
  | "draft"
  | "in_review"
  | "approved"
  | "published"
  | "review_overdue"
  | "retired" {
  const map: Record<
    string,
    | "draft"
    | "in_review"
    | "approved"
    | "published"
    | "review_overdue"
    | "retired"
  > = {
    DRAFT: "draft",
    IN_REVIEW: "in_review",
    APPROVED: "approved",
    PUBLISHED: "published",
    REVIEW_OVERDUE: "review_overdue",
    RETIRED: "retired",
    WITHDRAWN: "retired",
  };

  const key = String(s ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return map[key] ?? "draft";
}

export function formatDate(d: string | null): string {
  if (!d || d === EMPTY_PLACEHOLDER) return EMPTY_PLACEHOLDER;
  return d.split("T")[0].split("-").reverse().join("/");
}

export function formatDateTime(d: string | null): string {
  if (!d) return EMPTY_PLACEHOLDER;
  const date = new Date(d);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  return `${day}/${month}/${year} ${hour12}:${minutes} ${ampm}`;
}

export function formatDocType(t: string): string {
  return DOCUMENT_TYPES.find((d) => d.value === t)?.label ?? t;
}

/** Validate a title. Returns an error string, or null when valid. */
export function getTitleValidationError(raw: string): string | null {
  const title = raw.trim();
  if (!title) return null; // emptiness is handled by the required/disabled state
  if (title.length < 3) return "Title must be at least 3 characters.";
  if (!/[a-zA-Z0-9]/.test(title))
    return "Title must contain letters or numbers.";
  // Distinct non-space characters: a single repeated character has only 1.
  const distinct = new Set(title.replace(/\s+/g, "").toLowerCase()).size;
  if (distinct < 2)
    return "Enter a meaningful title, not a single repeated character.";
  return null;
}

/** Map a doc-engine list record to the shared GovDocRow shape. */
export function toGovDocRowFromDocEngine(
  d: DocEngineDocumentRecord,
): GovDocRow {
  const type = (d.documentType || "").toUpperCase();
  return {
    id: d.documentId,
    title: d.title ?? "",
    documentType: type,
    status: d.status ?? "",
    currentVersion: 1,
    versionCount: 1,
    reviewDate: EMPTY_PLACEHOLDER,
    createdAt: formatDate(d.createdAt ?? null),
    updatedAt: formatDate(d.updatedAt ?? null),
    daysUntilReview: null,
    reviewDueSoon: false,
    hasFile: !!d.filename,
    // Extras kept for client-side search / display.
    organization: d.organization ?? "",
    filename: d.filename ?? "",
    description: d.description ?? "",
    reviewCycle: d.reviewCycle ?? "",
    // Withdrawn rows explain themselves in the publication panel.
    retirementReason: d.retirementReason ?? "",
  };
}


export function isInValidationStatus(status: unknown): boolean {
  const value = String(status ?? "").trim().toLowerCase();
  return value === "draft" || value === "in-validation";
}

export function isDraftStatus(status: unknown): boolean {
  return String(status ?? "").trim().toLowerCase() === "draft";
}

export function toEvidenceRow(d: SupportingEvidence): EvidenceRow {
  return {
    id: d.id,
    title: d.title,
    description: "",
    tags: d.tags,
    hasFile: d.hasFile,
    fileName: d.fileName,
    fileSize: d.fileSize,
    contentType: d.contentType,
    createdAt: formatDate(d.createdAt),
    updatedAt: formatDate(d.updatedAt),
  };
}

/** Format a millisecond duration as a compact human string ("450 ms", "2.3 s", "1m 4s"). */
export function formatElapsed(ms: number | undefined | null): string {
  if (ms === null || ms === undefined || Number.isNaN(ms) || ms <= 0) {
    return EMPTY_PLACEHOLDER;
  }
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const mins = Math.floor(ms / 60_000);
  const secs = Math.round((ms % 60_000) / 1000);
  return `${mins}m ${secs}s`;
}

export function formatFileSize(bytes: number | null): string {
  if (!bytes) return EMPTY_PLACEHOLDER;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function getAuthUserDisplayName(user: AuthUser | null): string | null {
  if (!user) return null;
  return (
    user.displayName?.trim() ||
    user.username?.trim() ||
    user.email?.trim() ||
    null
  );
}

export function getHistoryActorName(
  event: HistoryEvent,
  currentUser: AuthUser | null,
): string {
  const actorName = event.performedByName?.trim();
  const currentUserName = getAuthUserDisplayName(currentUser);
  const actorId = event.performedBy?.trim();
  const currentUserIds = [
    currentUser?.userId,
    currentUser?.username,
    currentUser?.email,
  ].filter(Boolean);
  const matchesCurrentUser = currentUserIds.some((id) => id === actorId);
  const userDrivenEvents = new Set([
    "CREATED",
    "UPDATED",
    "SUBMITTED",
    "APPROVED",
    "REJECTED",
    "PUBLISHED",
    "RETIRED",
    "VERSION_CREATED",
  ]);

  if (
    currentUserName &&
    (matchesCurrentUser ||
      (!actorName && userDrivenEvents.has(event.eventType)) ||
      (actorName?.toLowerCase() === "system" &&
        userDrivenEvents.has(event.eventType)))
  ) {
    return currentUserName;
  }

  return actorName || "System";
}

/** Strip fancy Unicode (dashes, ellipsis, smart quotes) to ASCII-safe. */
export function plainText(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .replace(/\s*[—–]\s*/g, " ")
    .replace(/…/g, "...")
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/ {2,}/g, " ")
    .trim();
}

export interface LayerRowStateInput {
  isRunning: boolean;
  isComplete: boolean;
  isLayerError: boolean;
  layersErrored: boolean;
  layersSettled: boolean;
  notEvaluated: boolean;
  cleared: boolean;
}

/** Collapse the seven-layer row's boolean matrix into a single state key. */
export function resolveLayerRowState(input: LayerRowStateInput): LayerRowState {
  if (input.isRunning) return LAYER_ROW_STATE.RUNNING;
  if (input.isComplete) return LAYER_ROW_STATE.COMPLETE;
  if (input.isLayerError) return LAYER_ROW_STATE.FAILED;
  if (input.layersErrored) return LAYER_ROW_STATE.NOT_RUN;
  if (!input.layersSettled) return LAYER_ROW_STATE.PENDING;
  if (input.notEvaluated) return LAYER_ROW_STATE.NOT_EVALUATED;
  if (input.cleared) return LAYER_ROW_STATE.CLEARED;
  return LAYER_ROW_STATE.NOT_CLEARED;
}

/* ── Helper: lifecycle action label ── */
export function lifecycleActionLabel(action: string): string {
  const labels: Record<string, string> = {
    submit: "Submit for Review",
    approve: "Approve",
    publish: "Publish",
    reject: "Reject",
    "new-version": "Add New Version",
    retire: "Withdraw",
  };
  return labels[action] ?? action;
}

/* ── Shared layer-intelligence drawer helpers ── */

/** Format a 0..1 fraction as "NN%", or the placeholder when nullish. */
export function formatPercent(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return EMPTY_PLACEHOLDER;
  return `${Math.round(v * 100)}%`;
}

/** Title-case a snake_case, kebab-case, ALL_CAPS or space-separated identifier. */
export function toTitleCaseFromSnake(s: string | null | undefined): string {
  const raw = String(s ?? "").trim();
  if (!raw) return EMPTY_PLACEHOLDER;
  return raw
    .toLowerCase()
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/* ── Percentage tone tiers (classes live in constants.ts) ── */
export function pctTier(pct: number): Tier {
  return pct >= PCT_GOOD_MIN ? "good" : pct >= PCT_OK_MIN ? "ok" : "risk";
}

/* ── Domain Intelligence helpers ── */
export function pctValue(v?: number | null): number | null {
  if (v === null || v === undefined || Number.isNaN(v)) return null;
  return Math.max(0, Math.min(1, v));
}

export function strengthLabel(conf?: number | null): StrengthLabel {
  const c = pctValue(conf);
  if (c === null) return EMPTY_PLACEHOLDER;
  if (c >= CONFIDENCE_STRONG_THRESHOLD) return "Strong";
  if (c >= CONFIDENCE_MODERATE_THRESHOLD) return "Moderate";
  if (c > 0) return "Weak";
  return EMPTY_PLACEHOLDER;
}

export function confTone(conf?: number | null) {
  const c = pctValue(conf);
  if (c === null) return CONF_TONES.none;
  if (c >= CONFIDENCE_STRONG_THRESHOLD) return CONF_TONES.strong;
  if (c >= CONFIDENCE_MODERATE_THRESHOLD) return CONF_TONES.moderate;
  return CONF_TONES.weak;
}

/* ── Pipeline yield / tone helpers ── */
export function buildResultTone(
  result: ValidateFileResult | null | undefined,
  phase: ConsolePhase,
): ConsoleTone {
  if (phase === JOB_PHASE.ERROR) return "fail";
  if (!result) return "neutral";

  const job = String(result.processingJob?.status ?? "").toUpperCase();
  if (job === "FAILED" || job === "ERROR" || job === "PARTIAL") return "fail";
  if ((result.summary?.fail ?? 0) > 0) return "fail";
  if ((result.summary?.warn ?? 0) > 0) return "neutral";
  return "pass";
}
export interface ConsoleRuleCounts {
  total: number;
  passed: number;
  failed: number;
}

export function buildYield(
  result: ValidateFileResult | null,
  phase: ConsolePhase = "running",
  counts: ConsoleRuleCounts | null = null,
): ConsoleYield[] {
  const s = result?.summary;
  const ly = result?.liveYield;
  /* Counts only fill gaps — a real validate result always wins. */
  const domain =
    !result && counts && counts.total > 0 ? counts : null;
  const total = ly?.totalRules?.count ?? s?.total ?? domain?.total ?? 0;
  const passed = ly?.passed?.count ?? s?.pass ?? domain?.passed ?? 0;
  const failed =
    ly?.failedAndWarning?.failedCount ?? s?.fail ?? domain?.failed ?? 0;
  const warnings = ly?.failedAndWarning?.warningCount ?? s?.warn ?? 0;
  const rawScore =
    ly?.compliance?.percentage ??
    (ly?.compliance?.score !== undefined
      ? ly.compliance.score * 100
      : total > 0
        ? (passed / total) * 100
        : 0);
  const score = Math.round(rawScore);
  /* `haveNumbers` — either source gave us real figures to render. */
  const haveNumbers = !!result || !!domain;
  const errored = phase === JOB_PHASE.ERROR && !haveNumbers;
  const pending = !errored && !haveNumbers;
  const emptyHint = errored ? "not available" : pending ? "in progress" : "";
  const show = (n: number) => (haveNumbers ? String(n) : EMPTY_PLACEHOLDER);

  const skipped = s?.skipped ?? 0;
  const applicable = Math.max(0, total - skipped);
  const totalHint = domain
    ? "checked for this domain"
    : haveNumbers && skipped > 0
      ? `${skipped} not applicable`
      : "";
  const complianceHint = haveNumbers
    ? applicable > 0
      ? `${passed} of ${applicable} applicable rule${applicable === 1 ? "" : "s"} covered`
      : "no applicable rules"
    : errored
      ? "not available"
      : "";
  const passedHint = domain
    ? "rules covered in this document"
    : haveNumbers
      ? total > 0
        ? `of ${total} total rule${total === 1 ? "" : "s"}`
        : ""
      : emptyHint;
  const failedHint = domain ? "rules not covered in this document" : emptyHint;
  void warnings;

  return [
    {
      label: "Total Rules",
      value: show(total),
      hint: totalHint || emptyHint,
      tone: "neutral",
    },
    {
      label: "Compliance",
      value: haveNumbers ? `${score}%` : EMPTY_PLACEHOLDER,
      hint: complianceHint,
      tone: !haveNumbers
        ? "neutral"
        : score >= 80
          ? "pass"
          : score >= 50
            ? "warn"
            : "fail",
    },
    {
      label: "Covered",
      value: show(passed),
      hint: passedHint,
      tone: "pass",
    },
    {
      label: "Not Covered",
      value: show(failed),
      hint: failedHint,
      tone: "fail",
    },
  ];
}

/* ── Pipeline stage-state helpers ── */
export function normalizeStageKey(key: string | undefined): string {
  const k = String(key ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s&-]+/g, "_");
  return STAGE_KEY_ALIASES[k] ?? k;
}

export function buildStageStates(
  result: ValidateFileResult | null,
): ConsoleStageState[] | null {
  const job = result?.processingJob;
  const apiStages = result?.pipelineStages ?? [];
  if (!job && apiStages.length === 0) return null;

  const byKey = new Map<string, string>();
  for (const s of apiStages) byKey.set(normalizeStageKey(s.key), String(s.status ?? ""));

  const jobStatus = String(job?.status ?? "").toUpperCase();
  const succeeded = jobStatus === "SUCCESS" || jobStatus === "COMPLETED";
  const halted = jobStatus === "PARTIAL" || jobStatus === "FAILED" || jobStatus === "ERROR";

  const currentIndex = PIPELINE_STAGES.findIndex(
    (s) => s.key === normalizeStageKey(job?.currentStage),
  );
  const lastReached = halted
    ? Math.min(
      currentIndex >= 0 ? currentIndex : HOLD_STAGE_INDEX,
      HOLD_STAGE_INDEX,
    )
    : currentIndex;

  return PIPELINE_STAGES.map((stage, i) => {
    const reported = byKey.get(stage.key);
    if (reported) {
      const st = reported.trim().toLowerCase();
      if (["completed", "complete", "success", "passed", "done"].includes(st))
        return "done";
      if (["failed", "error"].includes(st)) return "failed";
      if (["running", "in_progress", "active"].includes(st)) return "active";
      return "pending";
    }
    if (succeeded) return "done";
    if (lastReached < 0) return "pending";
    if (i < lastReached) return "done";
    if (i === lastReached) return halted ? "failed" : "active";
    return "pending";
  });
}

export function buildStageStatesFromEvents(
  events: Record<string, IngestStageEvent> | null | undefined,
): ConsoleStageState[] | null {
  if (!events || Object.keys(events).length === 0) return null;

  const byUiKey = new Map<string, IngestStageEvent>();
  for (const evt of Object.values(events)) {
    if (!evt?.stage) continue;
    byUiKey.set(normalizeStageKey(evt.stage), evt);
  }

  const states: ConsoleStageState[] = PIPELINE_STAGES.map((stage) => {
    const evt = byUiKey.get(stage.key);
    if (!evt) return "pending";
    const st = String(evt.status ?? "").trim().toLowerCase();
    if (evt.finished === true) return "done";
    if (["completed", "complete", "success", "passed", "done"].includes(st))
      return "done";
    if (["failed", "error"].includes(st)) return "failed";
    if (["running", "in_progress", "active", "pending"].includes(st))
      return "active";
    return "pending";
  });

  const firstNotDone = states.findIndex((s) => s !== "done");
  if (firstNotDone >= 0 && states[firstNotDone] === "pending") {
    states[firstNotDone] = "active";
  }
  return states;
}

/* ── Extraction feed helpers ── */
export function sortedCategoryKeys(
  byCat: Record<string, ValidateRuleResult[]>,
): string[] {
  const known = CATEGORY_ORDER.filter((c) => (byCat[c]?.length ?? 0) > 0);
  const extra = Object.keys(byCat)
    .filter(
      (c) =>
        !CATEGORY_ORDER.includes(c as (typeof CATEGORY_ORDER)[number]) &&
        (byCat[c]?.length ?? 0) > 0,
    )
    .sort();
  return [...known, ...extra];
}

export function categoryTone(summary: ValidateFileSummary): {
  dot: string;
  border: string;
  bg: string;
} {
  if ((summary.fail ?? 0) > 0)
    return {
      dot: "bg-red-500",
      border: "border-red-200/60 dark:border-red-500/20",
      bg: "bg-red-50/60 dark:bg-red-500/[0.05]",
    };
  if ((summary.warn ?? 0) > 0)
    return {
      dot: "bg-amber-500",
      border: "border-amber-200/60 dark:border-amber-500/20",
      bg: "bg-amber-50/60 dark:bg-amber-500/[0.05]",
    };
  if ((summary.pass ?? 0) > 0)
    return {
      dot: "bg-emerald-500",
      border: "border-emerald-200/60 dark:border-emerald-500/20",
      bg: "bg-emerald-50/60 dark:bg-emerald-500/[0.05]",
    };
  return {
    dot: "bg-slate-400",
    border: "border-border",
    bg: "bg-[#f8fafc] dark:bg-white/[0.02]",
  };
}

export function summaryText(summary: ValidateFileSummary): string {
  const parts: string[] = [];
  if (summary.pass) parts.push(`${summary.pass} passed`);
  if (summary.fail) parts.push(`${summary.fail} failed`);
  if (summary.warn)
    parts.push(`${summary.warn} warning${summary.warn === 1 ? "" : "s"}`);
  if (summary.skipped) parts.push(`${summary.skipped} skipped`);
  return parts.join(" · ") || `${summary.total} total`;
}

export function countForFilter(
  summary: ValidateFileSummary,
  filter: "ALL" | "PASS" | "FAIL" | "WARN" | "SKIPPED",
): number {
  switch (filter) {
    case "ALL":
      return summary.total;
    case "PASS":
      return summary.pass;
    case "FAIL":
      return summary.fail;
    case "WARN":
      return summary.warn;
    case "SKIPPED":
      return summary.skipped;
  }
}

/** Set PDF /Title metadata so viewers show `fileName` instead of "(anonymous)". */
export async function withPdfTitle(
  blob: Blob,
  fileName: string | null,
): Promise<Blob> {
  if (!fileName) return blob;
  const title = fileName.replace(/\.[^./\\]+$/, "") || fileName;
  try {
    const bytes = await blob.arrayBuffer();
    const pdf = await PDFDocument.load(bytes, { updateMetadata: false });
    pdf.setTitle(title, { showInWindowTitleBar: true });
    const out = await pdf.save();
    // Uint8Array is a valid BlobPart at runtime; cast for TS's stricter typing.
    return new Blob([out as BlobPart], { type: "application/pdf" });
  } catch {
    return blob;
  }
}

 

/**
 * Check if the user has the "document:view" permission.
 * Strictly checks permissions returned from /api/v1/users/current/permissions.
 */
export const hasDocumentViewPermission = (permissions?: unknown): boolean => {
  if (!Array.isArray(permissions) || permissions.length === 0) {
    return false;
  }
  return permissions.some((p: any) => {
    if (!p) return false;
    if (typeof p === "string") {
      const s = p.trim().toLowerCase();
      return s === "document:view" || s === "document:*" || s === "*:*";
    }
    if (typeof p === "object") {
      const code = typeof p.code === "string" ? p.code.trim().toLowerCase() : "";
      if (code === "document:view" || code === "document:*" || code === "*:*") return true;

      const module = typeof p.module === "string" ? p.module.trim().toLowerCase() : "";
      const action = typeof p.action === "string" ? p.action.trim().toLowerCase() : "";
      if (
        (module === "document" && (action === "view" || action === "*")) ||
        (module === "*" && (action === "view" || action === "*"))
      ) {
        return true;
      }

      const perm = typeof p.permission === "string" ? p.permission.trim().toLowerCase() : "";
      if (perm === "document:view" || perm === "document:*" || perm === "*:*") return true;

      const name = typeof p.name === "string" ? p.name.trim().toLowerCase() : "";
      if (name === "document:view" || name === "document:*" || name === "*:*") return true;
    }
    return false;
  });
};